import { useEffect, useState } from 'react';
import { api, type TenantInfo } from '../lib/api';

/**
 * After auth, fetch the tenants this agent may access (GET /agent/my-tenants),
 * enriched with branding. Falls back to the codes already present in the JWT
 * claim (name = code) so the picker still works if the call fails.
 *
 * @param ready  gate the fetch until the user is signed in
 * @param claimCodes  tenant codes parsed from the token claim (allowedTenants)
 */
export function useTenants(
  ready: boolean,
  claimCodes: string[]
): { tenants: TenantInfo[]; loading: boolean } {
  const fallback: TenantInfo[] = claimCodes.map((code) => ({ code, name: code }));
  const [tenants, setTenants] = useState<TenantInfo[]>(fallback);
  const [loading, setLoading] = useState(ready);

  const claimKey = claimCodes.join(',');

  useEffect(() => {
    if (!ready) {
      setTenants(claimCodes.map((code) => ({ code, name: code })));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .myTenants()
      .then((res) => {
        if (cancelled) return;
        const items = res.items ?? [];
        setTenants(items.length > 0 ? items : claimCodes.map((code) => ({ code, name: code })));
      })
      .catch(() => {
        if (cancelled) return;
        setTenants(claimCodes.map((code) => ({ code, name: code })));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, claimKey]);

  return { tenants, loading };
}
